import { HeaderComponent, HeaderState, LayoutType } from "../types";

interface ImportResult {
	success: boolean;
	state?: HeaderState;
	error?: string;
}

/**
 * Parses HTML and CSS from an existing header and reconstructs the HeaderState for editing
 */
export const importHeaderFromCode = (
	html: string,
	css: string
): ImportResult => {
	try {
		// Create a temporary DOM parser to work with the HTML
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");

		// Find the header element
		const headerElement = doc.querySelector("header.header-container");
		if (!headerElement) {
			return {
				success: false,
				error: 'No header element found. Please ensure the HTML contains a <header class="header-container"> element.',
			};
		}

		// Extract basic header information
		const containerType = headerElement.querySelector(
			".header-inner-container"
		)
			? "container"
			: "full";

		// Parse CSS into a manageable format
		const cssRules = parseCSS(css);

		// Extract header styles
		const headerStyles = extractStyles(cssRules, ".header-container");

		// Determine if header is sticky
		const isSticky = headerStyles.position === "sticky";

		// Determine layout type
		const layoutType = determineLayoutType(headerElement, cssRules);

		// Extract components
		const components = extractComponents(headerElement, cssRules);

		// Construct the HeaderState
		const state: HeaderState = {
			components,
			layout: layoutType as LayoutType,
			styles: headerStyles,
			containerType,
			isSticky,
			history: [],
			currentHistoryIndex: -1,
		};

		return {
			success: true,
			state,
		};
	} catch (error) {
		console.error("Error importing header:", error);
		return {
			success: false,
			error: `Failed to import header: ${
				error instanceof Error ? error.message : String(error)
			}`,
		};
	}
};

/**
 * Parse CSS string into a map of selectors to style objects
 */
const parseCSS = (css: string): Map<string, Record<string, string>> => {
	const styleMap = new Map<string, Record<string, string>>();

	// Remove comments
	css = css.replace(/\/\*[\s\S]*?\*\//g, "");

	// Extract rules
	const ruleRegex = /([^{]+){\s*([^}]+)}/g;
	let match;

	while ((match = ruleRegex.exec(css)) !== null) {
		const selectors = match[1]
			.trim()
			.split(",")
			.map((s) => s.trim());
		const declarations = match[2].trim();

		// Parse declarations into style object
		const styleObj: Record<string, string> = {};
		const declarationRegex = /([^:]+):([^;]+);?/g;
		let declarationMatch;

		while (
			(declarationMatch = declarationRegex.exec(declarations)) !== null
		) {
			const property = declarationMatch[1].trim();
			const value = declarationMatch[2].trim();
			styleObj[property] = value;
		}

		// Add to map for each selector
		for (const selector of selectors) {
			styleMap.set(selector, styleObj);
		}
	}

	return styleMap;
};

/**
 * Extract styles for a specific selector from parsed CSS
 */
const extractStyles = (
	cssRules: Map<string, Record<string, string>>,
	selector: string
): Record<string, string> => {
	const styles: Record<string, string> = {};

	// Get direct styles
	const directStyles = cssRules.get(selector);
	if (directStyles) {
		Object.assign(styles, directStyles);
	}

	// Get related styles (like media queries)
	Array.from(cssRules.entries()).forEach(([ruleSelector, ruleStyles]) => {
		if (ruleSelector.includes(selector) && ruleSelector !== selector) {
			// Don't merge media queries or pseudo selectors directly
			// Just note they exist for specific properties
			if (ruleSelector.includes("@media") || ruleSelector.includes(":")) {
			} else {
				// Merge any other related styles
				Object.assign(styles, ruleStyles);
			}
		}
	});

	return styles;
};

/**
 * Determine the layout type based on HTML structure and CSS
 */
const determineLayoutType = (
	headerElement: Element,
	cssRules: Map<string, Record<string, string>>
): string => {
	const layoutElement = headerElement.querySelector(".header-layout");
	if (!layoutElement) return "standard";

	// Check layout styles
	const layoutStyles = extractStyles(cssRules, ".header-layout");

	// Look for distinctive structure patterns
	if (
		layoutElement.querySelector(".header-layout-left") &&
		layoutElement.querySelector(".header-layout-right")
	) {
		return "split";
	}

	if (
		layoutElement.querySelector(".header-layout-top") &&
		layoutElement.querySelector(".header-layout-bottom")
	) {
		return "stacked";
	}

	// Check CSS properties
	if (layoutStyles.justifyContent === "center") {
		return "centered";
	}

	if (layoutStyles.flexDirection === "column") {
		return "stacked";
	}

	// Default to standard
	return "standard";
};

/**
 * Extract components from the header element
 */
const extractComponents = (
	headerElement: Element,
	cssRules: Map<string, Record<string, string>>
): HeaderComponent[] => {
	const components: HeaderComponent[] = [];

	// Find all component elements with header-{type} classes
	const componentElements =
		headerElement.querySelectorAll('[class*="header-"]');

	componentElements.forEach((element, index) => {
		// Try to determine component type from class
		const classes = Array.from(element.classList);
		const typeClass = classes.find(
			(className) =>
				className.startsWith("header-logo-") ||
				className.startsWith("header-searchBar-") ||
				className.startsWith("header-navItems-") ||
				className.startsWith("header-wishlist-") ||
				className.startsWith("header-cart-") ||
				className.startsWith("header-user-") ||
				className.startsWith("header-menu-")
		);

		if (!typeClass) return; // Skip if not a recognized component

		// Extract type and ID from class name
		const typeParts = typeClass.split("-");
		const type = typeParts[1] as any;
		const idNumber = typeParts[2];
		const id = `${type}-${idNumber}`;

		// Extract styles
		const styles = extractStyles(cssRules, `.${typeClass}`);

		// Create component based on type
		const component: HeaderComponent = {
			id,
			type,
			styles,
			settings: extractComponentSettings(
				element,
				type,
				cssRules,
				typeClass
			),
		};

		// Extract responsive visibility
		extractVisibilitySettings(component, cssRules, typeClass);

		components.push(component);
	});

	return components;
};

/**
 * Extract component-specific settings
 */
const extractComponentSettings = (
	element: Element,
	type: string,
	cssRules: Map<string, Record<string, string>>,
	className: string
): any => {
	const settings: any = {};

	switch (type) {
		case "logo":
			// Extract logo settings
			const imgElement = element.querySelector("img");
			if (imgElement) {
				settings.image = imgElement.getAttribute("src") || "";

				// Determine size based on CSS or image dimensions
				const styles = extractStyles(cssRules, `.${className} img`);
				settings.size = determineSize(styles.maxWidth || "120px");
			}

			// Check if logo is in an anchor
			const anchorElement = element.closest("a");
			if (anchorElement) {
				settings.link = anchorElement.getAttribute("href") || "/";
			} else {
				settings.link = "/";
			}
			break;

		case "searchBar":
			// Determine if full or icon
			settings.size = element.classList.contains("search-button")
				? "icon"
				: "full";

			// Get placeholder text
			const inputElement = element.querySelector("input");
			if (inputElement) {
				settings.placeholder =
					inputElement.getAttribute("placeholder") || "Search...";
			}

			// Determine design from styles
			const containerStyles = extractStyles(
				cssRules,
				`.${className} .search-input-container`
			);
			settings.design = determineSearchDesign(containerStyles);

			// Extract icon
			settings.icon = "search"; // Default
			break;

		case "navItems":
			// Extract nav items
			const items: any[] = [];
			const links = element.querySelectorAll("a.nav-item");
			links.forEach((link, i) => {
				items.push({
					id: `nav-item-${Date.now()}-${i}`,
					text: link.textContent || `Item ${i + 1}`,
					link: link.getAttribute("href") || "/",
				});
			});
			settings.items = items;

			// Extract font settings
			const navItemStyles = extractStyles(
				cssRules,
				`.${className} .nav-item`
			);
			settings.fontWeight = navItemStyles.fontWeight || "normal";
			settings.textDecoration = navItemStyles.textDecoration || "none";
			settings.fontStyle = navItemStyles.fontStyle || "normal";

			// Extract hover color
			Array.from(cssRules.entries()).forEach(([selector, styles]) => {
				if (selector.includes(`.${className} .nav-item:hover`)) {
					settings.hoverColor = styles.color || "#3b82f6";
					return; // Stop searching after finding the first match
				}
			});
			break;

		case "wishlist":
		case "cart":
		case "user":
		case "menu":
			// Extract button settings
			const buttonStyles = extractStyles(
				cssRules,
				`.${className}.header-icon-button`
			);

			// Determine shape
			settings.shape = determineButtonShape(buttonStyles);

			// Extract border radius if rounded
			if (settings.shape === "rounded") {
				settings.borderRadius = parseInt(
					buttonStyles.borderRadius || "4px"
				);
			}

			// Background color
			settings.background = buttonStyles.background || "transparent";

			// Get hover color
			Array.from(cssRules.entries()).forEach(([selector, styles]) => {
				if (
					selector.includes(`.${className}.header-icon-button:hover`)
				) {
					settings.hoverColor = styles.backgroundColor || "#f3f4f6";
					return; // Stop searching after finding the first match
				}
			});

			// Determine hover animation
			settings.hoverAnimation = determineHoverAnimation(
				cssRules,
				className
			);

			// Determine type and icon
			settings.type = "button";
			settings.icon = type; // Default to component type

			break;

		default:
			break;
	}

	return settings;
};

/**
 * Extract responsive visibility settings
 */
const extractVisibilitySettings = (
	component: HeaderComponent,
	cssRules: Map<string, Record<string, string>>,
	className: string
): void => {
	const hideInSizes: string[] = [];

	// Check for media queries that hide this component
	Array.from(cssRules.entries()).forEach(([selector, styles]) => {
		if (
			selector.includes("@media") &&
			selector.includes(className) &&
			styles.display === "none"
		) {
			// Determine which size this media query corresponds to
			if (selector.includes("max-width: 480px")) {
				hideInSizes.push("XS");
			} else if (selector.includes("max-width: 640px")) {
				hideInSizes.push("S");
			} else if (selector.includes("max-width: 768px")) {
				hideInSizes.push("M");
			} else if (selector.includes("max-width: 1024px")) {
				hideInSizes.push("L");
			} else if (selector.includes("min-width: 1025px")) {
				hideInSizes.push("XL");
			}
		}
	});

	if (hideInSizes.length > 0) {
		component.hideInSizes = hideInSizes as any[];
	}
};

/**
 * Helper: Determine button shape from styles
 */
const determineButtonShape = (styles: Record<string, string>): string => {
	const borderRadius = styles.borderRadius || "";

	if (borderRadius === "0" || borderRadius === "0px") {
		return "box";
	} else if (borderRadius === "50%") {
		return "circle";
	} else {
		return "rounded";
	}
};

/**
 * Helper: Determine search design from styles
 */
const determineSearchDesign = (styles: Record<string, string>): string => {
	// Look for distinctive design patterns
	if (!styles.border && styles.borderBottom) {
		return "minimal";
	} else if (styles.borderRadius && styles.borderRadius.includes("9999px")) {
		return "rounded";
	} else if (
		styles.background &&
		styles.background !== "transparent" &&
		!styles.border
	) {
		return "filled";
	} else if (styles.boxShadow && styles.boxShadow !== "none") {
		return "shadowed";
	}

	return "default";
};

/**
 * Helper: Determine hover animation type
 */
const determineHoverAnimation = (
	cssRules: Map<string, Record<string, string>>,
	className: string
): string => {
	// Check for spin animation
	if (cssRules.has(`.${className}.header-icon-button:hover svg`)) {
		const svgHoverStyles = cssRules.get(
			`.${className}.header-icon-button:hover svg`
		);
		if (svgHoverStyles && svgHoverStyles.animation) {
			return "spin";
		}
	}

	// Check for scale transform (pop)
	const hoverStyles = cssRules.get(`.${className}.header-icon-button:hover`);
	if (hoverStyles) {
		if (hoverStyles.transform && hoverStyles.transform.includes("scale")) {
			return "pop";
		}

		if (hoverStyles.animation && hoverStyles.animation.includes("flash")) {
			return "flash";
		}

		if (hoverStyles.opacity && hoverStyles.opacity !== "1") {
			return "fade";
		}
	}

	return "none";
};

/**
 * Helper: Determine size category from dimension
 */
const determineSize = (dimension: string): string => {
	const size = parseInt(dimension);

	if (size <= 60) return "XS";
	if (size <= 80) return "S";
	if (size <= 120) return "M";
	if (size <= 160) return "L";
	return "XL";
};
